import type { LucideIcon } from "lucide-react";
import type React from "react";
import { cn } from "../../../lib/utils";

interface LoginTextFieldProps {
	id: string;
	label: string;
	type?: React.HTMLInputTypeAttribute;
	name: string;
	value: string;
	onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	onBlur: () => void;
	placeholder?: string;
	autoComplete?: string;
	inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
	disabled?: boolean;
	error?: string;
	errorId?: string;
	icon: LucideIcon;
	trailing?: React.ReactNode;
}

export const LoginTextField = ({
	id,
	label,
	type = "text",
	name,
	value,
	onChange,
	onBlur,
	placeholder,
	autoComplete,
	inputMode,
	disabled,
	error,
	errorId,
	icon: Icon,
	trailing,
}: LoginTextFieldProps) => {
	const hasError = Boolean(error);

	return (
		<div className="space-y-1.5">
			<label htmlFor={id} className="block text-sm font-medium text-primary">
				{label}
			</label>
			<div className="relative">
				<div
					className={cn(
						"pointer-events-none absolute inset-y-0 left-0 flex w-11 items-center justify-center",
						hasError ? "text-status-invite-expired" : "text-icon",
					)}
				>
					<Icon className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden />
				</div>
				<input
					id={id}
					type={type}
					name={name}
					value={value}
					onChange={onChange}
					onBlur={onBlur}
					placeholder={placeholder}
					autoComplete={autoComplete}
					inputMode={inputMode}
					spellCheck={false}
					disabled={disabled}
					aria-invalid={hasError}
					aria-describedby={hasError ? errorId : undefined}
					className={cn(
						"h-12 w-full rounded-[var(--radius-input)] border bg-bg-input pl-11 text-sm text-input-text shadow-sm transition-all duration-200",
						"placeholder:text-placeholder",
						"hover:border-neutral-300 hover:bg-white",
						"focus-visible:border-accent focus-visible:bg-white focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-accent/10",
						"disabled:cursor-not-allowed disabled:opacity-60",
						trailing ? "pr-11" : "pr-3.5",
						hasError
							? "border-status-invite-expired/60 bg-[#fff8f3] focus-visible:ring-status-invite-expired/15"
							: "border-border",
					)}
				/>
				{trailing ? (
					<div className="absolute inset-y-0 right-0 flex items-center pr-1">
						{trailing}
					</div>
				) : null}
			</div>
			{hasError ? (
				<p
					id={errorId}
					className="text-xs font-medium text-status-invite-expired"
					role="alert"
				>
					{error}
				</p>
			) : null}
		</div>
	);
};
